import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './App.css';
import { MotionProvider } from './motion';

// Import components
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { ContactModalProvider, useContactModal } from './components/ContactModal';

// Import pages
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import GrowthServices from './pages/GrowthServices';
import ServiceTerms from './pages/ServiceTerms';
import CaseStudies from './pages/CaseStudies';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogPostClinicAI from './pages/BlogPostClinicAI';
import BlogPostADMDirective from './pages/BlogPostADMDirective';
import BlogPostConnectedCare from './pages/BlogPostConnectedCare';
import BlogPostBurnout from './pages/BlogPostBurnout';
import Demos from './pages/Demos';
import VideoDetail from './pages/VideoDetail';
import MoneliLLM from './pages/MoneliLLM';
import PrivacyPolicy from './pages/PrivacyPolicy';
import SecurityPolicy from './pages/SecurityPolicy';
import AccessibilityStatement from './pages/AccessibilityStatement';

function RedirectHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const redirectPath = sessionStorage.getItem('redirectPath');
    if (redirectPath && redirectPath !== '/') {
      sessionStorage.removeItem('redirectPath');
      navigate(redirectPath, { replace: true });
    }
  }, [navigate]);

  return null;
}

function ExternalRedirect({ to }) {
  useEffect(() => {
    // Use replace() so Back doesn't bounce the user back into the redirect route.
    window.location.replace(to);
  }, [to]);

  return null;
}

// Floating Contact button: opens the contact form dialog (still a link to /contact without JS).
function ContactFloat() {
  const { open } = useContactModal();
  return (
    <div className="chat-float-stack">
      <a
        href="/contact"
        className="telegram-float"
        aria-label="Contact Moneli"
        aria-haspopup="dialog"
        onClick={(e) => { e.preventDefault(); open(e.currentTarget); }}
      >
        <i className="fa-solid fa-envelope" aria-hidden="true"></i>
        <span>Contact</span>
      </a>
    </div>
  );
}

function App() {
  // Reveals, counters and other scroll motion live in the motion layer (src/motion).

  useEffect(() => {
    // GA4 click tracking
    const onDocumentClick = (e) => {
      const anchor = e.target.closest('a');
      if (!anchor || typeof window.gtag !== 'function') return;

      const href = (anchor.getAttribute('href') || '').trim();
      const linkText = (anchor.textContent || '').trim();

      if (href.includes('calendar.app.google')) {
        window.gtag('event', 'book_demo_click', { link_text: linkText, link_url: href });
      } else if (href.startsWith('mailto:')) {
        window.gtag('event', 'email_click', { link_text: linkText, link_url: href });
      } else if (href.startsWith('tel:')) {
        window.gtag('event', 'phone_click', { link_text: linkText, link_url: href });
      } else if (href.startsWith('#') || href.startsWith('/')) {
        window.gtag('event', 'internal_nav_click', { link_text: linkText, link_url: href });
      }
    };

    document.addEventListener('click', onDocumentClick);
    return () => document.removeEventListener('click', onDocumentClick);
  }, []);

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RedirectHandler />
      <MotionProvider />
      <div className="App">
        <ContactModalProvider>
        <a className="skip-link" href="#main">Skip to content</a>
        <Navbar />

        <main id="main">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/services/web-marketing" element={<GrowthServices />} />
          <Route path="/services/:serviceId" element={<ServiceDetail />} />
          <Route path="/solutions" element={<Services />} />
          <Route path="/case-studies" element={<CaseStudies />} />
          {/* Student Housing redirects */}
          <Route
            path="/case-studies/housing"
            element={<ExternalRedirect to="https://housing-695c47.gitlab.io/" />}
          />
          <Route
            path="/housing"
            element={<ExternalRedirect to="https://housing-695c47.gitlab.io/" />}
          />

          {/* AI Ethics course case study redirect */}
          <Route
            path="/case-studies/ethics"
            element={<ExternalRedirect to="https://ethical-course-bf8542.gitlab.io/" />}
          />

          {/* EASCDI project redirect */}
          <Route
            path="/eascdi"
            element={<ExternalRedirect to="https://eascdi-36e747.gitlab.io/" />}
          />

          {/* SCC AMT redirect */}
          <Route
            path="/scc"
            element={<ExternalRedirect to="https://assessment-management-tool-ba4973.gitlab.io/" />}
          />

          {/* DLC redirect */}
          <Route
            path="/dlc"
            element={<ExternalRedirect to="https://distance-learning-f2b023.gitlab.io/" />}
          />

          {/* Vaughan compliance app redirect */}
          <Route
            path="/compliance"
            element={<ExternalRedirect to="https://vaughan-zoning-compliance-engine-c81f81.gitlab.io/" />}
          />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/ai-automation-for-canadian-clinics" element={<BlogPostClinicAI />} />
          <Route path="/blog/canadas-automated-decision-making-directive" element={<BlogPostADMDirective />} />
          <Route path="/blog/connected-care-for-canadians-act" element={<BlogPostConnectedCare />} />
          <Route path="/blog/clinician-burnout-ai-automation" element={<BlogPostBurnout />} />
          <Route path="/monelillm" element={<MoneliLLM />} />
          <Route path="/demos" element={<Demos />} />
          {/* Permanent shareable link per video — stays valid if the YouTube
              upload behind it is ever replaced. See src/data/videos.js. */}
          <Route path="/demos/video/:slug" element={<VideoDetail />} />
          <Route path="/terms" element={<ServiceTerms />} />
          <Route path="/terms/:serviceId" element={<ServiceTerms />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/security-policy" element={<SecurityPolicy />} />
          <Route path="/accessibility-statement" element={<AccessibilityStatement />} />
          <Route path="/contact" element={<Contact />} />
        </Routes>
        </main>

        <ContactFloat />

        <Footer />
        </ContactModalProvider>
      </div>
    </Router>
  );
}

export default App;